import type { ReactNode } from "react";
import SubSectionHeading from "../SubSectionHeading/SubSectionHeading";
import { InfoCard } from "./InlineSearchResults";

export interface AlgoliaSearchResultsProps<T> {
  results: T[];
  query: string;
  isLoading: boolean;
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
  handleItemSelected: (hit: T) => void;
  itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  resultsContainerRef: React.RefObject<HTMLDivElement>;
  renderHit: (hit: T, onSelect: (hit: T) => void, query?: string) => JSX.Element;
  isExactMatch?: (hit: T, query: string) => boolean;
  noResultsMessage?: string;
  loadingMessage?: string;
  className?: string;
  useOptionRole?: boolean;
  /**
   * Rendered under the empty / no-exact-match InfoCard and above suggestions.
   * Use for follow-up actions such as a CCMS lookup CTA.
   */
  noMatchExtra?: ReactNode;
}

export default function AlgoliaSearchResults<T extends Record<string, any>>({
  results,
  query,
  isLoading,
  highlightedIndex,
  setHighlightedIndex,
  handleItemSelected,
  itemRefs,
  resultsContainerRef,
  renderHit,
  isExactMatch,
  noResultsMessage = "No results found",
  loadingMessage = "Searching...",
  className,
  useOptionRole = true,
  noMatchExtra
}: AlgoliaSearchResultsProps<T>) {
  const renderHitOption = (hit: T, index: number) => (
    <div
      key={hit.objectID ?? `hit-${index}`}
      role={useOptionRole ? "option" : undefined}
      aria-selected={useOptionRole ? index === highlightedIndex : undefined}
      className={`rounded-lg p-2 transition-colors hover:bg-sys-brand-surface-container-high ${
        index === highlightedIndex ? "bg-sys-brand-surface-container-high" : ""
      }`}
      onMouseEnter={() => setHighlightedIndex(index)}
      ref={(el) => (itemRefs.current[index] = el)}
    >
      {renderHit(hit, () => handleItemSelected(hit), query)}
    </div>
  );

  if (isLoading) {
    return (
      <div ref={resultsContainerRef} className={className}>
        <div className="py-3 text-center text-sys-brand-on-surface-variant">{loadingMessage}</div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div ref={resultsContainerRef} className={className}>
        <InfoCard>{noResultsMessage}</InfoCard>
        {noMatchExtra}
      </div>
    );
  }

  if (!isExactMatch) {
    return (
      <div ref={resultsContainerRef} className={className}>
        {results.map((hit, index) => renderHitOption(hit, index))}
      </div>
    );
  }

  const exactMatches: { hit: T; originalIndex: number }[] = [];
  const suggestions: { hit: T; originalIndex: number }[] = [];

  results.forEach((hit, index) => {
    if (isExactMatch(hit, query)) {
      exactMatches.push({ hit, originalIndex: index });
    } else {
      suggestions.push({ hit, originalIndex: index });
    }
  });

  return (
    <div ref={resultsContainerRef} className={className}>
      {exactMatches.length > 0 ? (
        exactMatches.map(({ hit, originalIndex }) => renderHitOption(hit, originalIndex))
      ) : (
        <div className="mb-2">
          <InfoCard>No exact match found</InfoCard>
          {noMatchExtra}
        </div>
      )}

      {suggestions.length > 0 && (
        <>
          <SubSectionHeading className="py-2 px-1">
            {exactMatches.length > 0 ? "Other Results" : "Suggestions"}
          </SubSectionHeading>
          {suggestions.map(({ hit, originalIndex }) => renderHitOption(hit, originalIndex))}
        </>
      )}
    </div>
  );
}
